import { apiFetch } from '../hooks/useApi';
import { storageGet, storageSet } from './storage';

/**
 * Company billing profile — the branding + business details that appear on
 * generated PDF quotes and invoices, and the company name shown across the
 * workspace. The canonical copy lives server-side; we keep a per-user
 * localStorage mirror (utils/storage) so the PDF generator has it synchronously.
 *
 * There is NO sample fallback: before the profile has loaded (or if it fails),
 * `name` is '' and every other field is empty. Callers must handle the blank —
 * e.g. `profile.name || 'Your company'` in UI, or leave the line off a PDF.
 *
 *   getCompanyProfile()          → mirror (always an object, fields may be '')
 *   hasCompanyProfile()          → true once a real profile has been cached
 *   fetchCompanyProfile()        → server copy (updates mirror); on failure the cached/empty
 *                                  profile, or null with { nullOnError: true }
 *   saveCompanyProfile(p)        → write mirror only
 *   putCompanyProfile(p)         → save to server + mirror; throws on failure
 */
const KEY = 'company_profile';

export const DEFAULT_PROFILE = Object.freeze({
  name: '',
  logo: '',                 // data: URI
  address: '',              // multi-line
  email: '',
  phone: '',
  website: '',
  vatNumber: '',
  companyNumber: '',
  vatRate: 20,              // % — UK standard rate is a real default, not sample data
  paymentTermsDays: 30,
  bankName: '',
  bankSortCode: '',
  bankAccount: '',
  accent: '#1A80E5',
  footerNote: 'Thank you for your business.',
});

export function hasCompanyProfile() {
  const cached = storageGet(KEY, null);
  return !!(cached && typeof cached === 'object');
}

export function getCompanyProfile() {
  const cached = storageGet(KEY, null);
  if (!cached || typeof cached !== 'object') return { ...DEFAULT_PROFILE };
  return { ...DEFAULT_PROFILE, ...cached };
}

export function saveCompanyProfile(profile) {
  const merged = { ...DEFAULT_PROFILE, ...(profile || {}) };
  storageSet(KEY, merged);
  return merged;
}

/**
 * Load the canonical profile from the server and refresh the mirror.
 * On failure resolves the last cached copy (fields '' if never cached), or
 * `null` when called with `{ nullOnError: true }` so the caller can show an error.
 */
export async function fetchCompanyProfile({ nullOnError = false } = {}) {
  try {
    const d = await apiFetch('/api/contractor/company-profile');
    if (d?.profile) return saveCompanyProfile(d.profile);
  } catch { /* fall through */ }
  return nullOnError ? null : getCompanyProfile();
}

/**
 * Persist branding to the server (name is fixed at setup, so the server ignores
 * it) and update the mirror. Broadcasts `cw:company-updated` so the header
 * refreshes. Throws if the server rejects it — the mirror is only updated on success.
 */
export async function putCompanyProfile(profile) {
  const merged = { ...DEFAULT_PROFILE, ...(profile || {}) };
  await apiFetch('/api/contractor/company-profile', { method: 'PUT', body: JSON.stringify(merged) });
  saveCompanyProfile(merged);
  try { window.dispatchEvent(new Event('cw:company-updated')); } catch { /* no window */ }
  return merged;
}

export default getCompanyProfile;
